import React, { useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Button,
} from '@mui/material';
import { useSignupExperienceConfig } from '../hooks/useSignupExperienceConfig';

function TermsPage() {
  const { config: signupConfig, loading: signupConfigLoading } = useSignupExperienceConfig();
  const freeSignupTokens = signupConfig.freeSignupTokens;
  const historyCostTokens = signupConfig.historyCostTokens;
  const numberFormatter = useMemo(() => new Intl.NumberFormat('en-GB'), []);
  const formattedFreeSignupTokens = signupConfigLoading
    ? '…'
    : numberFormatter.format(freeSignupTokens);
  const formattedHistoryCostTokens = signupConfigLoading
    ? '…'
    : numberFormatter.format(historyCostTokens);
  const historyTokenDescription = signupConfigLoading
    ? '… tokens'
    : `${formattedHistoryCostTokens} token${historyCostTokens === 1 ? '' : 's'}`;

  const sections = useMemo(() => ([
    {
      title: '1. Introduction',
      paragraphs: [
        'These Terms and Conditions ("Terms") govern your use of the Virtual Medic platform, including the Unity application, web portal, and related services (collectively, the "Services").',
        'By creating an account or using the Services you agree to abide by these Terms, our Privacy Notice, and any policies referenced herein. If you do not agree, you must not access or use the Services.'
      ]
    },
    {
      title: '2. Accounts & Eligibility',
      paragraphs: [
        'Individual learners may register directly subject to the global user capacity limits defined by Virtual Medic. Institutions may only register via an authorised university administrator.',
        'You are responsible for maintaining accurate account information, safeguarding your credentials, and ensuring that all users within your institution comply with these Terms.'
      ],
      bullets: [
        `Individual accounts receive ${formattedFreeSignupTokens} complimentary tokens upon registration.`,
        'Institutional accounts must be linked to a verified university domain or invite.',
        'We may suspend or terminate access if we detect misuse, security risks, or breach of contract.'
      ]
    },
    {
      title: '3. Credit Wallet & Billing',
      paragraphs: [
        'Virtual Medic uses a pay-as-you-go token system for individual users. Tokens may be purchased through approved credit packs configured by the global administrator.',
        `Each completed patient history in the Unity app deducts ${historyTokenDescription} from the individual wallet. Once a wallet reaches zero tokens you must purchase additional credits before continuing.`
      ],
      bullets: [
        'Starter Pack: 50 tokens for £2.50 (10 histories equivalent).',
        'Standard Pack: 125 tokens for £5.00 (25 histories equivalent).',
        'Advanced Pack: 250 tokens for £9.00 (50 histories equivalent).',
        'Pro Pack: 500 tokens for £16.00 (100 histories equivalent).'
      ],
      footer: 'All pricing is inclusive of applicable VAT unless stated otherwise. Receipts and VAT invoices are issued via Stripe following successful payment.'
    },
    {
      title: '4. Institutional Billing & OpenAI Usage',
      paragraphs: [
        'University administrators may connect their own OpenAI API credentials to power realtime encounters and automated marking. Institutions are responsible for ensuring their OpenAI usage complies with OpenAI policies and local regulations.',
        'If an institution elects to use Virtual Medic-managed OpenAI infrastructure, associated usage fees and credit consumption will be governed by the institution\'s service agreement.'
      ]
    },
    {
      title: '5. Refunds & Chargebacks',
      paragraphs: [
        'Refund requests must be submitted through the Virtual Medic global admin team within 14 days of purchase and before credits are consumed.',
        'We reserve the right to decline refund requests where usage indicates material consumption of the service or where fraud is suspected. Approved refunds will reverse associated tokens and issue a Stripe refund to the original payment method.'
      ]
    },
    {
      title: '6. Content & Intellectual Property',
      paragraphs: [
        'All platform content—including cases, characters, templates, and analytics—is owned by Virtual Medic or the respective institution. Unauthorised reproduction, distribution, or derivative works are prohibited.',
        'Institutions retain ownership of content they author within their tenant. Virtual Medic may access tenant content solely to provide support, ensure compliance, and enhance product quality.'
      ]
    },
    {
      title: '7. Acceptable Use',
      paragraphs: [
        'You must not use the Services to harass, discriminate, or otherwise harm patients, learners, or staff. Attempts to reverse engineer, circumvent security controls, or misuse OpenAI credentials are strictly forbidden.',
        'We monitor for abuse and may revoke access, delete content, or notify authorities where legally required.'
      ]
    },
    {
      title: '8. Service Availability & Modifications',
      paragraphs: [
        'We strive to provide reliable access but do not guarantee uninterrupted availability. Maintenance windows and outages will be communicated via the in-app notification centre or email.',
        'Virtual Medic may update features, pricing, or policies from time to time. Material changes to these Terms will be communicated with reasonable notice.'
      ]
    },
    {
      title: '9. Data Protection & Privacy',
      paragraphs: [
        'Virtual Medic processes limited personal data to deliver the Services. Our Privacy Notice explains how data is collected, used, and stored.',
        'Institutions integrating their own OpenAI credentials are responsible for ensuring lawful data transfers and compliance with applicable privacy legislation.'
      ]
    },
    {
      title: '10. Limitation of Liability',
      paragraphs: [
        'To the maximum extent permitted by law, Virtual Medic is not liable for indirect, incidental, or consequential damages arising from use of the Services.',
        'Our total liability for any claim relating to these Terms shall not exceed the amount paid to Virtual Medic for the Services in the preceding twelve (12) months.'
      ]
    },
    {
      title: '11. Governing Law & Dispute Resolution',
      paragraphs: [
        'These Terms are governed by the laws of England and Wales. Any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.',
        'Parties agree to attempt informal resolution in good faith before commencing formal proceedings.'
      ]
    },
    {
      title: '12. Contact',
      paragraphs: [
        'For questions about these Terms, billing inquiries, or support requests, please contact support@virtualmedic.uk.'
      ]
    }
  ]), [formattedFreeSignupTokens, historyTokenDescription]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <Box sx={{ py: 6 }}>
      <Container maxWidth="md">
        <Typography variant="h3" component="h1" gutterBottom>
          Terms &amp; Conditions
        </Typography>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 2,
            '@media print': { display: 'none' },
          }}
        >
          <Button variant="outlined" color="primary" onClick={handlePrint}>
            Download / Print PDF
          </Button>
        </Box>
        <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>
        {sections.map((section) => (
          <Box key={section.title} sx={{ mb: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              {section.title}
            </Typography>
            {section.paragraphs.map((paragraph) => (
              <Typography key={paragraph} variant="body1" sx={{ mb: 2 }}>
                {paragraph}
              </Typography>
            ))}
            {section.bullets && (
              <List sx={{ listStyleType: 'disc', pl: 4 }}>
                {section.bullets.map((bullet) => (
                  <ListItem key={bullet} sx={{ display: 'list-item', pl: 0 }}>
                    <ListItemText primaryTypographyProps={{ variant: 'body1' }} primary={bullet} />
                  </ListItem>
                ))}
              </List>
            )}
            {section.footer && (
              <Typography variant="body2" color="text.secondary">
                {section.footer}
              </Typography>
            )}
          </Box>
        ))}
      </Container>
    </Box>
  );
}

export default TermsPage;
